import { DIMENSION_KEYS } from './types';
import type {
  Answer,
  AssessmentModel,
  AssessmentResult,
  DimensionKey,
  DimensionScores,
  LevelId,
  ResultTypeId,
  Scenario,
} from './types';

const emptyScores = (): DimensionScores => ({
  understanding: 0,
  verification: 0,
  questionDesign: 0,
  independentThinking: 0,
  collaboration: 0,
});

export function answerQuality(scenario: Scenario, choiceId: string): 'strong' | 'mixed' | 'risk' {
  const choice = scenario.choices.find((entry) => entry.id === choiceId);
  if (!choice) return 'risk';
  const selected = DIMENSION_KEYS.reduce((sum, key) => sum + choice.scores[key], 0);
  const best = Math.max(
    ...scenario.choices.map((entry) =>
      DIMENSION_KEYS.reduce((sum, key) => sum + entry.scores[key], 0),
    ),
  );
  const ratio = best === 0 ? 0 : selected / best;
  if (ratio >= 0.82) return 'strong';
  if (ratio >= 0.3) return 'mixed';
  return 'risk';
}

export function boundaryPosition(scenario: Scenario, choiceId: string): number {
  const choice = scenario.choices.find((entry) => entry.id === choiceId);
  if (!choice) return 10;
  const humanAgency = choice.scores.independentThinking + choice.scores.verification;
  return Math.round(8 + (humanAgency / 8) * 84);
}

export function scoreAssessment(
  scenarios: Scenario[],
  answers: Answer[],
  model: AssessmentModel,
): AssessmentResult {
  const earned = emptyScores();
  const possible = emptyScores();
  const answerByScenario = new Map(answers.map((answer) => [answer.scenarioId, answer.choiceId]));

  for (const scenario of scenarios) {
    const choiceId = answerByScenario.get(scenario.id);
    const choice = scenario.choices.find((entry) => entry.id === choiceId);
    if (!choice) throw new Error(`answer_missing:${scenario.id}`);

    for (const key of DIMENSION_KEYS) {
      earned[key] += choice.scores[key];
      possible[key] += Math.max(...scenario.choices.map((entry) => entry.scores[key]));
    }
  }

  const dimensions = emptyScores();
  for (const key of DIMENSION_KEYS) {
    dimensions[key] = possible[key] === 0 ? 0 : Math.round((earned[key] / possible[key]) * 100);
  }
  const total = Math.round(
    DIMENSION_KEYS.reduce((sum, key) => sum + dimensions[key], 0) / DIMENSION_KEYS.length,
  );
  const ranked = [...DIMENSION_KEYS].sort((a, b) => dimensions[b] - dimensions[a]);
  const type = model.types.find((entry) => total >= entry.min && total <= entry.max);
  const level = [...model.levels].reverse().find((entry) => total >= entry.min);
  if (!type || !level) throw new Error('result_classification_failed');

  return {
    total,
    dimensions,
    typeId: type.id,
    levelId: level.id,
    levelNumber: level.number,
    percentile: referencePercentile(total, model.benchmark.points),
    strongest: ranked[0] ?? 'understanding',
    growthEdge: ranked.at(-1) ?? 'collaboration',
  };
}

export function referencePercentile(
  score: number,
  points: Array<{ score: number; percentile: number }>,
): number {
  const clamped = Math.max(0, Math.min(100, score));
  const sorted = [...points].sort((a, b) => a.score - b.score);
  const upperIndex = sorted.findIndex((point) => point.score >= clamped);
  if (upperIndex <= 0) return sorted[0]?.percentile ?? 0;
  if (upperIndex === -1) return sorted.at(-1)?.percentile ?? 100;
  const lower = sorted[upperIndex - 1];
  const upper = sorted[upperIndex];
  if (!lower || !upper) return 0;
  const progress = (clamped - lower.score) / (upper.score - lower.score);
  return Math.round(lower.percentile + progress * (upper.percentile - lower.percentile));
}

export function parseSharedResult(search: string): {
  typeId: ResultTypeId;
  score: number;
  percentile: number;
} | null {
  const params = new URLSearchParams(search);
  const type = params.get('result');
  const rawScore = Number(params.get('score'));
  const rawPercentile = Number(params.get('p'));
  const allowed: ResultTypeId[] = ['passenger', 'operator', 'navigator', 'collaborator'];
  if (!allowed.includes(type as ResultTypeId) || !Number.isFinite(rawScore)) return null;
  return {
    typeId: type as ResultTypeId,
    score: Math.max(0, Math.min(100, Math.round(rawScore))),
    percentile: Number.isFinite(rawPercentile)
      ? Math.max(0, Math.min(100, Math.round(rawPercentile)))
      : 0,
  };
}

export function buildShareUrl(
  base: string,
  typeId: ResultTypeId,
  score: number,
  percentile: number,
  locale: string,
): string {
  const url = new URL(base);
  url.search = '';
  url.hash = '';
  url.searchParams.set('result', typeId);
  url.searchParams.set('score', String(Math.round(score)));
  url.searchParams.set('p', String(Math.round(percentile)));
  url.searchParams.set('lang', locale);
  return url.toString();
}

export function nextLevel(
  currentLevelNumber: number,
  model: AssessmentModel,
): { id: LevelId; number: number; min: number } | null {
  return model.levels.find((entry) => entry.number > currentLevelNumber) ?? null;
}

export function bucketScore(score: number): string {
  const floor = Math.floor(Math.max(0, Math.min(100, score)) / 10) * 10;
  return `${floor}-${Math.min(100, floor + 9)}`;
}

export type { DimensionKey };
